import React, { useEffect, useState } from 'react';
import { Building2, Home, Store, RefreshCw } from 'lucide-react';
import { apiService } from '@/lib/api';

const money = (n) => '$' + Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const money2 = (n) => '$' + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const num = (n) => Number(n || 0).toLocaleString();

// Per-unit cost breakdown for multi-unit / mixed-use projects. Reporting view
// only — draws and Draw Score stay whole-project. Hidden when a project has no
// unit types defined (legacy / single-structure projects).
export default function UnitCostBreakdown({ projectId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiService.getProjectUnitCosts(projectId);
      setData(res.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (projectId) load(); /* eslint-disable-next-line */ }, [projectId]);

  if (loading || !data || !data.has_units) return null;

  const { rows = [], totals = {} } = data;

  return (
    <div className="bg-white rounded-xl border border-slate-200" data-testid="unit-cost-breakdown">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-slate-500" />
          <h3 className="font-semibold text-slate-900 text-sm">Cost per unit</h3>
          <span className="text-[11px] text-slate-400">Reporting only · draws stay whole-project</span>
        </div>
        <button onClick={load} className="text-slate-400 hover:text-slate-700" title="Refresh" data-testid="unit-cost-refresh">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-slate-100 border-b border-slate-100">
        <Metric label="Total Units" value={num(totals.total_units)} />
        <Metric label="Total Sq Ft" value={num(totals.total_unit_sqft)} />
        <Metric label="Project Total" value={money(totals.project_total)} />
        <Metric label="Avg / Unit" value={money(totals.avg_cost_per_unit)} tone="text-blue-600" />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="unit-cost-table">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-200 text-left">
              <th className="px-3 py-2">Unit</th>
              <th className="px-3 py-2 text-right">Qty</th>
              <th className="px-3 py-2 text-right">Sq Ft</th>
              <th className="px-3 py-2 text-right">% Shared</th>
              <th className="px-3 py-2 text-right">Shared Share</th>
              <th className="px-3 py-2 text-right">Unit-Specific</th>
              <th className="px-3 py-2 text-right">Cost Each</th>
              <th className="px-3 py-2 text-right">$/Sq Ft</th>
              <th className="px-3 py-2 text-right">Subtotal</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.unit_id} className="border-b border-slate-50" data-testid={`unit-cost-row-${r.unit_id}`}>
                <td className="px-3 py-2 font-medium text-slate-800">
                  <span className="inline-flex items-center gap-1.5">
                    {r.category === 'commercial' ? <Store className="w-3.5 h-3.5 text-slate-400" /> : <Home className="w-3.5 h-3.5 text-slate-400" />}
                    {r.label}
                  </span>
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-600">{r.quantity}</td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-600">{num(r.sqft_each)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-500">{r.pct_shared}%</td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-600">{money2(r.shared_share)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-600">{money2(r.unit_specific)}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-900">{money2(r.cost_each)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-500">{money2(r.cost_per_sqft)}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-900">{money(r.subtotal)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-slate-200 bg-slate-50">
              <td className="px-3 py-2 font-semibold text-slate-700" colSpan={8}>Total</td>
              <td className="px-3 py-2 text-right tabular-nums font-bold text-slate-900">{money(totals.subtotals_sum)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {!totals.reconciles && (
        <div className="px-4 py-2 text-[11px] text-amber-700 bg-amber-50 border-t border-amber-100">
          Note: per-unit subtotals ({money(totals.subtotals_sum)}) differ slightly from the project total ({money(totals.project_total)}) due to rounding.
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, tone }) {
  return (
    <div className="px-3 py-3 text-center">
      <div className={`text-lg font-bold ${tone || 'text-slate-900'}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mt-0.5">{label}</div>
    </div>
  );
}
